import axios from 'axios';
import { message } from 'antd';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 45000,
});

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 800;

// Transient failures (dropped mobile connection, restarted PHP worker, gateway
// timeout) should not be shown as an error until a couple of quiet retries fail.
function isRetryable(error) {
  const method = (error.config?.method || 'get').toLowerCase();
  if (method !== 'get' || error.config?.noRetry) return false;
  if (!error.response) return error.code !== 'ERR_CANCELED';
  return [429, 500, 502, 503, 504].includes(error.response.status);
}

// Build a consistent, user-readable error string that ALWAYS includes a code,
// so any failure is easy to identify (e.g. "Server band hai (HTTP 503)").
export function formatApiError(error, fallback = 'Something went wrong') {
  if (error && error.response) {
    const status = error.response.status;
    const data = error.response.data || {};
    const serverMsg = data.error || data.detail || data.message || error.response.statusText;
    return `${serverMsg || fallback} (HTTP ${status})`;
  }
  if (error && error.request) {
    // Request sent but no response — server down / unreachable / tunnel 5xx.
    return 'Could not connect to the server — the server may be down or there is a network issue (ERR_NETWORK)';
  }
  return `${(error && error.message) || fallback}`;
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // File uploads (FormData) can legitimately take many minutes on slow links;
  // the 45s default would abort them mid-transfer and surface as "failed".
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.timeout = 0;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response && error.response.status;

    if (isRetryable(error)) {
      const cfg = error.config;
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (cfg.__retryCount <= MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * cfg.__retryCount));
        return api(cfg);
      }
    }

    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Attach a normalized, code-bearing message every caller can show.
    error.uiMessage = formatApiError(error);
    // Surface "system" failures (server down / 5xx) globally with a code, so
    // even screens that swallow errors still tell the user what broke.
    if (!error.response || status >= 500) {
      message.error({ content: error.uiMessage, key: 'api-error' });
    }
    return Promise.reject(error);
  }
);

export default api;
